const MAX_PRODUCTS = 4;
const MAX_QUERY_LENGTH = 900;
const MAX_NAME_CANDIDATES = 10;

class ArticleProducts extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.initialised = true;
    this.abortController = new AbortController();

    this.productGrid = this.querySelector('[data-product-grid]');
    this.contextElement = this.querySelector('[data-article-context]');

    const configuredLimit = Number.parseInt(
      this.dataset.limit || String(MAX_PRODUCTS),
      10
    );

    this.productLimit = Math.min(
      Math.max(configuredLimit || MAX_PRODUCTS, 1),
      MAX_PRODUCTS
    );

    if (!this.productGrid || !this.contextElement) {
      this.hideSection();
      return;
    }

    /*
     * Detect fallback products already rendered
     * directly by Liquid.
     */
    this.hasInitialFallback =
      this.dataset.hasFallback === 'true' &&
      this.productGrid.children.length > 0;

    /*
     * Make the initial fallback carousel work
     * immediately while automatic recommendations load.
     */
    if (this.hasInitialFallback) {
      this.hidden = false;

      requestAnimationFrame(() => {
        this.initCarousel();
      });
    }

    this.loadProducts();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.initialised = false;
  }

  /**
   * Recommendation priority:
   *
   * 1. Article tag → product tag
   * 2. Product names / distinctive phrases in article
   * 3. Article tags → product title
   * 4. Article title keywords
   * 5. Article tag → vendor
   * 6. Article body keywords
   * 7. Fallback collection
   */
  async loadProducts() {
    const context = this.readArticleContext();

    const products = [];
    const usedProductIds = new Set();

    this.setAttribute('aria-busy', 'true');

    try {
      /*
       * ------------------------------------------------
       * 1. ARTICLE TAG → PRODUCT TAG
       * ------------------------------------------------
       */
      if (
        products.length < this.productLimit &&
        context.tags.length > 0
      ) {
        const results = await this.safeSearch(() =>
          this.findProducts(
            'tag',
            context.tags
          )
        );

        this.addUniqueProducts(
          products,
          results,
          usedProductIds
        );
      }

      /*
       * ------------------------------------------------
       * 2. PRODUCT NAMES / DISTINCTIVE PHRASES
       *
       * Examples:
       *
       * TYGA Gel Heel Protectors
       * XYZ
       * PF193
       * Nano Glass Foot File
       *
       * Each strong candidate is searched directly
       * against Shopify's product TITLE field.
       * ------------------------------------------------
       */
      if (products.length < this.productLimit) {
        const nameCandidates =
          this.extractProductNameCandidates(context);

        for (const candidate of nameCandidates) {
          if (products.length >= this.productLimit) {
            break;
          }

          const query =
            `title:"${this.escapeSearchValue(candidate)}"`;

          const results = await this.safeSearch(() =>
            this.fetchSearchProducts(query)
          );

          this.addUniqueProducts(
            products,
            results,
            usedProductIds
          );
        }
      }

      /*
       * ------------------------------------------------
       * 3. ARTICLE TAG → PRODUCT TITLE
       *
       * Useful if article tag is:
       *
       * Shoe Covers
       *
       * and a product is:
       *
       * Disposable Blue Shoe Covers
       * ------------------------------------------------
       */
      if (
        products.length < this.productLimit &&
        context.tags.length > 0
      ) {
        const results = await this.safeSearch(() =>
          this.findProducts(
            'title',
            context.tags
          )
        );

        this.addUniqueProducts(
          products,
          results,
          usedProductIds
        );
      }

      /*
       * ------------------------------------------------
       * 4. ARTICLE TITLE KEYWORDS
       * ------------------------------------------------
       */
      if (products.length < this.productLimit) {
        const titleKeywords =
          this.extractTitleKeywords(
            context.title
          );

        if (titleKeywords.length > 0) {
          const query =
            this.buildTextQuery(
              titleKeywords
            );

          if (query) {
            const results =
              await this.safeSearch(() =>
                this.fetchSearchProducts(query)
              );

            this.addUniqueProducts(
              products,
              results,
              usedProductIds
            );
          }
        }
      }

      /*
       * ------------------------------------------------
       * 5. ARTICLE TAG → PRODUCT VENDOR
       * ------------------------------------------------
       */
      if (
        products.length < this.productLimit &&
        context.tags.length > 0
      ) {
        const results = await this.safeSearch(() =>
          this.findProducts(
            'vendor',
            context.tags
          )
        );

        this.addUniqueProducts(
          products,
          results,
          usedProductIds
        );
      }

      /*
       * ------------------------------------------------
       * 6. ARTICLE BODY KEYWORDS
       * ------------------------------------------------
       */
      if (products.length < this.productLimit) {
        const contentKeywords =
          this.extractContentKeywords(
            context.content,
            context.title
          );

        if (contentKeywords.length > 0) {
          const query =
            this.buildTextQuery(
              contentKeywords
            );

          if (query) {
            const results =
              await this.safeSearch(() =>
                this.fetchSearchProducts(query)
              );

            this.addUniqueProducts(
              products,
              results,
              usedProductIds
            );
          }
        }
      }

      /*
       * ------------------------------------------------
       * AUTOMATIC RESULTS FOUND
       *
       * Replace the currently displayed fallback
       * products only if genuine automatic matches exist.
       * ------------------------------------------------
       */
      if (products.length > 0) {
        this.showProducts(
          products,
          'automatic'
        );

        return;
      }

      /*
       * ------------------------------------------------
       * 7. KEEP LIQUID-RENDERED FALLBACK
       * ------------------------------------------------
       */
      if (this.hasInitialFallback) {
        this.dataset.source = 'fallback';
        this.hidden = false;

        requestAnimationFrame(() => {
          this.initCarousel();
        });

        return;
      }

      /*
       * Legacy AJAX fallback.
       */
      const fallbackProducts =
        await this.fetchFallbackProducts();

      if (fallbackProducts.length > 0) {
        this.showProducts(
          fallbackProducts,
          'fallback'
        );

        return;
      }

      this.hideSection();
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      console.warn(
        '[Article products] Recommended products could not be loaded.',
        error
      );

      /*
       * Never destroy the server-rendered fallback
       * because an automatic search failed.
       */
      if (this.hasInitialFallback) {
        this.dataset.source = 'fallback';
        this.hidden = false;

        requestAnimationFrame(() => {
          this.initCarousel();
        });

        return;
      }

      this.hideSection();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Read title, article tags and article body.
   */
  readArticleContext() {
    try {
      const parsed = JSON.parse(
        this.contextElement.textContent.trim() || '{}'
      );

      return {
        title: String(
          parsed.title || ''
        ).trim(),

        tags: Array.isArray(parsed.tags)
          ? parsed.tags
              .map((tag) =>
                String(tag || '').trim()
              )
              .filter(Boolean)
          : [],

        content: String(
          parsed.content || ''
        ).trim()
      };
    } catch (error) {
      console.warn(
        '[Article products] Article context could not be parsed.',
        error
      );

      return {
        title: '',
        tags: [],
        content: ''
      };
    }
  }

  /**
   * Add products without duplicates.
   */
  addUniqueProducts(
    target,
    items,
    usedIds
  ) {
    for (const item of items) {
      const productId =
        item.dataset.productId;

      if (!productId) continue;
      if (usedIds.has(productId)) continue;
      if (!this.isValidProductItem(item)) continue;

      usedIds.add(productId);
      target.push(item);

      if (
        target.length >=
        this.productLimit
      ) {
        break;
      }
    }
  }

  /**
   * Words which should not influence
   * product recommendations.
   */
  getStopWords() {
    return new Set([
      'about',
      'after',
      'again',
      'against',
      'all',
      'also',
      'and',
      'any',
      'are',
      'because',
      'been',
      'before',
      'being',
      'best',
      'both',
      'but',
      'can',
      'could',
      'did',
      'does',
      'doing',
      'each',
      'easy',
      'for',
      'from',
      'get',
      'getting',
      'good',
      'great',
      'guide',
      'had',
      'has',
      'have',
      'having',
      'help',
      'here',
      'his',
      'how',
      'into',
      'its',
      'just',
      'make',
      'more',
      'most',
      'not',
      'now',
      'our',
      'out',
      'over',
      'product',
      'products',
      'should',
      'some',
      'than',
      'that',
      'the',
      'their',
      'them',
      'then',
      'there',
      'these',
      'they',
      'thing',
      'things',
      'this',
      'those',
      'through',
      'too',
      'under',
      'use',
      'used',
      'using',
      'very',
      'was',
      'way',
      'ways',
      'were',
      'what',
      'when',
      'where',
      'which',
      'while',
      'who',
      'why',
      'will',
      'with',
      'would',
      'you',
      'your'
    ]);
  }

  /**
   * Detect phrases which look like actual
   * product names or model numbers.
   *
   * Strong signals include:
   *
   * "Quoted Product Name"
   * TYGA Gel Heel Protectors
   * XYZ
   * PF193
   * H02
   */
  extractProductNameCandidates(context) {
    const candidates = new Map();

    const stopWords =
      this.getStopWords();

    const addCandidate = (
      rawValue,
      bonus = 0
    ) => {
      let value = String(
        rawValue || ''
      )
        .replace(/\s+/g, ' ')
        .replace(
          /^[\s,.;:!?()[\]{}'"“”‘’]+|[\s,.;:!?()[\]{}'"“”‘’]+$/g,
          ''
        )
        .trim();

      if (!value) return;
      if (value.length < 2) return;
      if (value.length > 90) return;

      const words =
        value.split(/\s+/);

      if (words.length > 7) {
        return;
      }

      const meaningfulWords =
        words.filter((word) => {
          const lower = word
            .toLowerCase()
            .replace(
              /[^a-z0-9+-]/g,
              ''
            );

          if (!lower) return false;

          return (
            lower.length >= 2 &&
            !stopWords.has(lower)
          );
        });

      if (meaningfulWords.length === 0) {
        return;
      }

      /*
       * Reject ordinary sentence-start words such
       * as "This", "When", "Using", etc.
       */
      if (
        words.length === 1 &&
        meaningfulWords[0].length < 4 &&
        !/[A-Z]{2,}/.test(value) &&
        !/\d/.test(value)
      ) {
        return;
      }

      let score = bonus;

      /*
       * Multi-word names are usually stronger.
       */
      score +=
        meaningfulWords.length * 5;

      /*
       * TYGA / brand-style mention.
       */
      if (/^TYGA\b/i.test(value)) {
        score += 30;
      }

      /*
       * ALL CAPS:
       *
       * XYZ
       * PU
       * EVA
       */
      if (
        /\b[A-Z]{2,}[A-Z0-9-]*\b/.test(
          value
        )
      ) {
        score += 25;
      }

      /*
       * Model/SKU style:
       *
       * PF193
       * EM147
       * H02
       */
      if (
        /[A-Za-z]+\d+|\d+[A-Za-z]+/.test(
          value
        )
      ) {
        score += 30;
      }

      /*
       * Longer specific names are generally
       * more useful than one generic word.
       */
      score += Math.min(
        value.length / 8,
        10
      );

      const key =
        value.toLocaleLowerCase();

      const existing =
        candidates.get(key);

      if (
        !existing ||
        score > existing.score
      ) {
        candidates.set(key, {
          value,
          score
        });
      }
    };

    /*
     * Article tags are also useful product-name
     * candidates.
     */
    for (const tag of context.tags) {
      addCandidate(
        tag,
        40
      );
    }

    const combinedText =
      `${context.title} ${context.content}`;

    /*
     * Quoted phrases.
     *
     * Example:
     * "Gel Heel Protectors"
     */
    const quotedPattern =
      /["“]([^"”]{2,90})["”]/g;

    for (
      const match of
      combinedText.matchAll(quotedPattern)
    ) {
      addCandidate(
        match[1],
        45
      );
    }

    /*
     * Capitalised product-name-looking phrases.
     *
     * Examples:
     *
     * TYGA Gel Heel Protectors
     * Nano Glass Foot File
     * Premium Shoe Covers
     */
    const capitalisedPattern =
      /\b(?:[A-Z][A-Za-z0-9+&'/-]*|[A-Z0-9]{2,})(?:\s+(?:[A-Z][A-Za-z0-9+&'/-]*|[A-Z0-9]{2,})){0,6}\b/g;

    for (
      const match of
      combinedText.matchAll(
        capitalisedPattern
      )
    ) {
      addCandidate(
        match[0],
        20
      );
    }

    /*
     * Explicit model/SKU/all-cap tokens.
     *
     * This is what makes something like XYZ,
     * PF193 or EM147 particularly powerful.
     */
    const distinctiveTokenPattern =
      /\b(?:[A-Z]{2,}[A-Z0-9-]*|[A-Za-z]{1,8}\d{1,8}[A-Za-z0-9-]*)\b/g;

    for (
      const match of
      combinedText.matchAll(
        distinctiveTokenPattern
      )
    ) {
      addCandidate(
        match[0],
        55
      );
    }

    return Array.from(
      candidates.values()
    )
      .sort(
        (a, b) =>
          b.score - a.score
      )
      .slice(
        0,
        MAX_NAME_CANDIDATES
      )
      .map(
        (item) => item.value
      );
  }

  /**
   * Convert normal prose into useful keywords.
   */
  tokenize(text) {
    const stopWords =
      this.getStopWords();

    return String(text || '')
      .toLowerCase()
      .replace(/&amp;/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(
        /[^a-z0-9\s'+&-]/g,
        ' '
      )
      .split(/\s+/)
      .map((word) =>
        word
          .replace(
            /^['&+-]+|['&+-]+$/g,
            ''
          )
          .trim()
      )
      .filter((word) => {
        if (!word) return false;

        /*
         * Keep short model numbers such as h02.
         */
        if (
          word.length < 3 &&
          !/\d/.test(word)
        ) {
          return false;
        }

        if (/^\d+$/.test(word)) {
          return false;
        }

        if (stopWords.has(word)) {
          return false;
        }

        return true;
      });
  }

  /**
   * Article title is a strong signal.
   */
  extractTitleKeywords(title) {
    const words =
      this.tokenize(title);

    return [
      ...new Set(words)
    ].slice(0, 10);
  }

  /**
   * Rank useful terms from the body.
   */
  extractContentKeywords(
    content,
    title
  ) {
    const words =
      this.tokenize(content);

    if (words.length === 0) {
      return [];
    }

    const titleWords =
      new Set(
        this.extractTitleKeywords(
          title
        )
      );

    const counts = new Map();

    for (const word of words) {
      counts.set(
        word,
        (counts.get(word) || 0) + 1
      );
    }

    const rankedWords =
      Array.from(
        counts.entries()
      )
        .sort((a, b) => {
          const wordA = a[0];
          const wordB = b[0];

          let scoreA = a[1];
          let scoreB = b[1];

          /*
           * Major boost if it also appears
           * in the article title.
           */
          if (
            titleWords.has(wordA)
          ) {
            scoreA += 8;
          }

          if (
            titleWords.has(wordB)
          ) {
            scoreB += 8;
          }

          /*
           * Model/SKU-like terms get a boost.
           */
          if (/\d/.test(wordA)) {
            scoreA += 10;
          }

          if (/\d/.test(wordB)) {
            scoreB += 10;
          }

          /*
           * Longer words tend to be more specific.
           */
          scoreA += Math.min(
            wordA.length / 5,
            3
          );

          scoreB += Math.min(
            wordB.length / 5,
            3
          );

          if (scoreB !== scoreA) {
            return scoreB - scoreA;
          }

          return (
            wordB.length -
            wordA.length
          );
        })
        .map(
          ([word]) => word
        );

    return [
      ...new Set(rankedWords)
    ].slice(0, 12);
  }

  /**
   * Normal product search:
   *
   * ("heel" OR "protectors" OR "shoe")
   */
  buildTextQuery(values) {
    const cleanValues = [
      ...new Set(
        values
          .map((value) =>
            String(value || '').trim()
          )
          .filter(Boolean)
      )
    ];

    if (cleanValues.length === 0) {
      return '';
    }

    const clauses =
      cleanValues.map((value) => {
        return `"${this.escapeSearchValue(value)}"`;
      });

    return `(${clauses.join(' OR ')})`;
  }

  /**
   * Search an exact Shopify field.
   *
   * Examples:
   *
   * tag:"Shoe Covers"
   * title:"Gel Heel Protectors"
   * vendor:"TYGA"
   */
  async findProducts(
    field,
    values
  ) {
    const queries =
      this.buildQueries(
        field,
        values
      );

    const products = [];
    const productIds =
      new Set();

    for (const query of queries) {
      const items =
        await this.fetchSearchProducts(
          query
        );

      for (const item of items) {
        const productId =
          item.dataset.productId;

        if (!productId) continue;

        if (
          productIds.has(productId)
        ) {
          continue;
        }

        if (
          !this.isValidProductItem(
            item
          )
        ) {
          continue;
        }

        productIds.add(productId);
        products.push(item);

        if (
          products.length >=
          this.productLimit
        ) {
          return products;
        }
      }
    }

    return products;
  }

  /**
   * Build field queries in safe-sized batches.
   */
  buildQueries(
    field,
    values
  ) {
    const clauses =
      values
        .map((value) =>
          String(value || '').trim()
        )
        .filter(Boolean)
        .map((value) => {
          const escapedValue =
            this.escapeSearchValue(
              value
            );

          return `${field}:"${escapedValue}"`;
        });

    const queries = [];
    let currentBatch = [];

    for (const clause of clauses) {
      const proposedBatch = [
        ...currentBatch,
        clause
      ];

      const proposedQuery =
        proposedBatch.join(' OR ');

      if (
        currentBatch.length > 0 &&
        proposedQuery.length >
          MAX_QUERY_LENGTH
      ) {
        queries.push(
          `(${currentBatch.join(' OR ')})`
        );

        currentBatch = [
          clause
        ];
      } else {
        currentBatch =
          proposedBatch;
      }
    }

    if (
      currentBatch.length > 0
    ) {
      queries.push(
        `(${currentBatch.join(' OR ')})`
      );
    }

    return queries;
  }

  /**
   * Escape Shopify search characters.
   */
  escapeSearchValue(value) {
    return String(value)
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/:/g, '\\:')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)');
  }

  /**
   * One failed matching method should not
   * prevent the other methods from running.
   */
  async safeSearch(callback) {
    try {
      return await callback();
    } catch (error) {
      if (
        error?.name ===
        'AbortError'
      ) {
        throw error;
      }

      console.warn(
        '[Article products] One matching method failed.',
        error
      );

      return [];
    }
  }

  /**
   * Request products through Shopify search.
   */
  async fetchSearchProducts(query) {
    const fallbackRoot =
      window.Shopify?.routes?.root ||
      '/';

    const configuredSearchUrl =
      this.dataset.searchUrl ||
      `${fallbackRoot}search`;

    const sectionName =
      this.dataset.searchSection ||
      'art-prod-search';

    const searchUrl =
      new URL(
        configuredSearchUrl,
        window.location.origin
      );

    searchUrl.searchParams.set(
      'q',
      query
    );

    searchUrl.searchParams.set(
      'type',
      'product'
    );

    searchUrl.searchParams.set(
      'options[prefix]',
      'none'
    );

    searchUrl.searchParams.set(
      'options[unavailable_products]',
      'hide'
    );

    searchUrl.searchParams.set(
      'sort_by',
      'relevance'
    );

    searchUrl.searchParams.set(
      'section_id',
      sectionName
    );

    return this.fetchSectionProducts(
      searchUrl
    );
  }

  /**
   * AJAX fallback for backwards compatibility.
   */
  async fetchFallbackProducts() {
    const fallbackUrl =
      this.dataset.fallbackUrl?.trim();

    if (!fallbackUrl) {
      return [];
    }

    const sectionName =
      this.dataset.fallbackSection ||
      'art-prod-fallback';

    const collectionUrl =
      new URL(
        fallbackUrl,
        window.location.origin
      );

    collectionUrl.searchParams.set(
      'section_id',
      sectionName
    );

    return this.fetchSectionProducts(
      collectionUrl
    );
  }

  /**
   * Fetch Shopify section HTML and extract
   * the generated Horizon product cards.
   */
  async fetchSectionProducts(url) {
    const response = await fetch(
      url.toString(),
      {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'default',
        signal:
          this.abortController.signal,
        headers: {
          Accept: 'text/html',
          'X-Requested-With':
            'XMLHttpRequest'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Shopify section request returned HTTP ${response.status}.`
      );
    }

    const responseHtml =
      await response.text();

    const responseDocument =
      new DOMParser()
        .parseFromString(
          responseHtml,
          'text/html'
        );

    this.installStyles(
      responseDocument
    );

    const responseList =
      responseDocument
        .querySelector(
          '[data-product-response]'
        );

    if (!responseList) {
      return [];
    }

    return Array.from(
      responseList.children
    )
      .filter((item) => {
        return (
          item instanceof
            HTMLElement &&
          item.hasAttribute(
            'data-product-item'
          )
        );
      })
      .map((item) =>
        document.importNode(
          item,
          true
        )
      )
      .filter((item) =>
        this.isValidProductItem(
          item
        )
      )
      .slice(
        0,
        this.productLimit
      );
  }

  /**
   * Bring dynamically returned Horizon
   * product-card CSS onto the article.
   */
  installStyles(
    responseDocument
  ) {
    const styles =
      responseDocument
        .querySelectorAll(
          'style'
        );

    for (const style of styles) {
      const css =
        style.textContent?.trim();

      if (!css) continue;

      const styleKey =
        this.hashString(css);

      const existingStyle =
        document.head
          .querySelector(
            `style[data-article-product-style="${styleKey}"]`
          );

      if (existingStyle) {
        continue;
      }

      const installedStyle =
        document.createElement(
          'style'
        );

      installedStyle
        .dataset
        .articleProductStyle =
          styleKey;

      installedStyle.textContent =
        css;

      document.head.appendChild(
        installedStyle
      );
    }
  }

  hashString(value) {
    let hash = 0;

    for (
      let index = 0;
      index < value.length;
      index += 1
    ) {
      hash =
        (
          hash * 31 +
          value.charCodeAt(index)
        ) >>> 0;
    }

    return hash.toString(36);
  }

  /**
   * Validate returned Horizon card.
   */
  isValidProductItem(item) {
    if (
      !(item instanceof HTMLElement)
    ) {
      return false;
    }

    if (
      !item.dataset.productId
    ) {
      return false;
    }

    const card =
      item.querySelector(
        'product-card, .product-card'
      );

    if (!card) {
      return false;
    }

    const hasLink =
      Boolean(
        card.querySelector(
          'a[href]'
        )
      );

    const hasImage =
      Boolean(
        card.querySelector(
          'img'
        )
      );

    const hasText =
      card.textContent
        .trim()
        .length > 0;

    return (
      hasLink ||
      hasImage ||
      hasText
    );
  }

  /**
   * Carousel / swipe / desktop drag.
   */
  initCarousel() {
    const grid =
      this.productGrid;

    if (
      !grid ||
      grid.dataset.carouselReady ===
        'true'
    ) {
      return;
    }

    grid.dataset.carouselReady =
      'true';

    let isDragging = false;
    let hasMoved = false;
    let startX = 0;
    let startScrollLeft = 0;

    grid.addEventListener(
      'dragstart',
      (event) => {
        event.preventDefault();
      }
    );

    grid.addEventListener(
      'pointerdown',
      (event) => {
        if (
          event.pointerType ===
          'touch'
        ) {
          return;
        }

        if (
          event.button !== 0
        ) {
          return;
        }

        if (
          event.target.closest(
            'button, input, select, textarea'
          )
        ) {
          return;
        }

        if (
          grid.scrollWidth <=
          grid.clientWidth + 2
        ) {
          return;
        }

        isDragging = true;
        hasMoved = false;

        startX =
          event.clientX;

        startScrollLeft =
          grid.scrollLeft;

        grid.classList.add(
          'is-dragging'
        );

        if (
          grid.setPointerCapture
        ) {
          grid.setPointerCapture(
            event.pointerId
          );
        }
      }
    );

    grid.addEventListener(
      'pointermove',
      (event) => {
        if (!isDragging) {
          return;
        }

        const movement =
          event.clientX -
          startX;

        if (
          Math.abs(movement) > 4
        ) {
          hasMoved = true;
          event.preventDefault();
        }

        grid.scrollLeft =
          startScrollLeft -
          movement;
      }
    );

    const finishDrag =
      (event) => {
        if (!isDragging) {
          return;
        }

        isDragging = false;

        grid.classList.remove(
          'is-dragging'
        );

        if (
          grid.releasePointerCapture &&
          grid.hasPointerCapture?.(
            event.pointerId
          )
        ) {
          grid.releasePointerCapture(
            event.pointerId
          );
        }
      };

    grid.addEventListener(
      'pointerup',
      finishDrag
    );

    grid.addEventListener(
      'pointercancel',
      finishDrag
    );

    grid.addEventListener(
      'click',
      (event) => {
        if (!hasMoved) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        hasMoved = false;
      },
      true
    );
  }

  /**
   * Replace fallback with automatic results.
   */
  showProducts(
    items,
    source
  ) {
    const validItems =
      items
        .filter((item) =>
          this.isValidProductItem(
            item
          )
        )
        .slice(
          0,
          this.productLimit
        );

    if (
      validItems.length === 0
    ) {
      /*
       * Keep existing fallback if one exists.
       */
      if (
        this.hasInitialFallback
      ) {
        this.dataset.source =
          'fallback';

        this.hidden = false;
        return;
      }

      this.hideSection();
      return;
    }

    this.productGrid
      .replaceChildren(
        ...validItems
      );

    this.dataset.source =
      source;

    this.hidden = false;

    /*
     * Event listeners stay attached to the UL,
     * so we only need to initialise once.
     */
    requestAnimationFrame(() => {
      this.initCarousel();
    });
  }

  hideSection() {
    /*
     * Never hide a valid server-rendered
     * fallback collection.
     */
    if (
      this.hasInitialFallback
    ) {
      this.dataset.source =
        'fallback';

      this.hidden = false;

      requestAnimationFrame(() => {
        this.initCarousel();
      });

      return;
    }

    this.dataset.source =
      'none';

    this.hidden = true;

    if (this.productGrid) {
      this.productGrid
        .replaceChildren();
    }
  }
}

if (
  !customElements.get(
    'article-products'
  )
) {
  customElements.define(
    'article-products',
    ArticleProducts
  );
}